import React, { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, ChevronRight } from "lucide-react";
import { services, findService } from "../data/services";
import { renderRichText } from "../utils/glossary";

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = findService(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <h1 className="section-title mb-4">Service not found</h1>
        <p className="text-[#a6b3c8] mb-8">The service you are looking for does not exist.</p>
        <Link to="/services" className="btn-primary">Back to Services</Link>
      </div>
    );
  }

  const related = services.filter((s) => s.slug !== service.slug);

  return (
    <div data-testid={`service-page-${service.slug}`} className="pt-24 pb-16">
      {/* Breadcrumb */}
      <div className="max-w-[1280px] mx-auto px-6 mb-8">
        <nav data-testid="breadcrumb" className="flex items-center gap-2 text-sm text-[#a6b3c8]" style={{ fontFamily: "Sora, sans-serif" }}>
          <Link to="/" className="hover:text-white">Home</Link>
          <ChevronRight size={14} className="opacity-60" />
          <Link to="/services" className="hover:text-white">Services</Link>
          <ChevronRight size={14} className="opacity-60" />
          <span className="text-white">{service.title}</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden mb-16" data-testid="service-hero">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="chip mb-5">
                <span className="chip-dot" />
                {service.tagline}
              </div>
              <h1 className="text-white font-bold mb-5" style={{ fontFamily: "Sora, sans-serif", fontSize: "clamp(34px, 4.5vw, 56px)", lineHeight: 1.05 }}>
                {service.title}
              </h1>
              <p className="text-[#c8d4ea] text-lg leading-relaxed mb-8 max-w-xl">
                {renderRichText(service.short)}
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/contact" data-testid="service-hero-cta" className="btn-primary">
                  Request a Consultation <ArrowRight size={18} />
                </Link>
                <button onClick={() => navigate(-1)} className="btn-secondary">
                  <ArrowLeft size={16} /> Back
                </button>
              </div>
            </div>
            <div className="relative">
              <div className="img-wrap aspect-[4/3] rounded-2xl" style={{ border: "1px solid var(--border-subtle)", background: service.imageFit === "contain" ? "linear-gradient(180deg, #0a1a30 0%, #05101f 100%)" : undefined }}>
                <img
                  src={service.image}
                  alt={service.title}
                  style={{
                    ...(service.imagePosition ? { objectPosition: service.imagePosition } : {}),
                    ...(service.imageFit ? { objectFit: service.imageFit } : {}),
                  }}
                />
                {service.imageFit !== "contain" && (
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 50%, rgba(5,16,31,0.75) 100%)" }} />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="mb-16">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <div className="section-eyebrow">Overview</div>
              <h2 className="section-title">What this service is really about</h2>
            </div>
            <div className="lg:col-span-2">
              <p className="text-[#c8d4ea] text-[17px] leading-[1.75]" data-testid="service-overview">
                {renderRichText(service.overview)}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Offerings grid */}
      <section className="mb-16">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="mb-8 max-w-3xl">
            <div className="section-eyebrow">What we offer</div>
            <h2 className="section-title">Capabilities and deliverables</h2>
            <p className="section-subtitle">
              A concise view of what a typical engagement in this practice looks like.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {service.offerings.map((o, i) => (
              <div key={o.title} data-testid={`offering-${i}`} className="glass-card p-6">
                <div className={`svc-icon ${service.iconCls} mb-4`} style={{ width: 42, height: 42 }}>{o.icon}</div>
                <h3 className="text-white font-semibold text-lg mb-2" style={{ fontFamily: "Sora, sans-serif" }}>{o.title}</h3>
                <p className="text-[#a6b3c8] text-[14px] leading-relaxed">{renderRichText(o.body)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Two-column capabilities + use cases */}
      <section className="mb-16">
        <div className="max-w-[1280px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="glass-card p-8" data-testid="service-capabilities">
            <div className="section-eyebrow mb-3">Detailed capabilities</div>
            <h3 className="text-white font-semibold text-2xl mb-5" style={{ fontFamily: "Sora, sans-serif" }}>
              Inside our practice
            </h3>
            <ul className="space-y-3">
              {service.capabilities.map((c) => (
                <li key={c} className="flex items-start gap-3 text-[#c8d4ea] text-[15px]">
                  <Check size={16} className="mt-1 shrink-0 text-[#6bd6bd]" />
                  <span>{renderRichText(c)}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="glass-card p-8" data-testid="service-usecases">
            <div className="section-eyebrow mb-3">Where it applies</div>
            <h3 className="text-white font-semibold text-2xl mb-5" style={{ fontFamily: "Sora, sans-serif" }}>
              Typical use cases
            </h3>
            <ul className="space-y-3">
              {service.useCases.map((u) => (
                <li key={u} className="flex items-start gap-3 text-[#c8d4ea] text-[15px]">
                  <ChevronRight size={16} className="mt-1 shrink-0 text-[#7fa5e6]" />
                  <span>{renderRichText(u)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Partners strip */}
      <section className="mb-16">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="glass-card p-8">
            <div className="section-eyebrow mb-3">Vendor ecosystem</div>
            <h3 className="text-white font-semibold text-2xl mb-6" style={{ fontFamily: "Sora, sans-serif" }}>
              Technologies we work with
            </h3>
            <div className="flex flex-wrap gap-3">
              {service.partners.map((p) => (
                <span key={p} className="px-4 py-2 rounded-full text-[13.5px] text-[#c8d4ea]" style={{ background: "rgba(59,116,209,0.10)", border: "1px solid rgba(59,116,209,0.28)", fontFamily: "Sora, sans-serif" }}>
                  {p}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="mb-16">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="section-eyebrow">Related</div>
              <h3 className="text-white font-semibold text-2xl" style={{ fontFamily: "Sora, sans-serif" }}>
                Other services you may need
              </h3>
            </div>
            <Link to="/services" className="btn-secondary" style={{ padding: "10px 18px", fontSize: 14 }}>
              All services <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            {related.map((r, i) => (
              <Link key={r.slug} to={`/services/${r.slug}`} data-testid={`related-${i}`} className="glass-card p-5 no-underline flex flex-col">
                <div className={`svc-icon ${r.iconCls} mb-4`} style={{ width: 40, height: 40 }}>{r.icon}</div>
                <div className="text-white font-semibold text-[14.5px] mb-1" style={{ fontFamily: "Sora, sans-serif" }}>{r.title}</div>
                <div className="text-[#a6b3c8] text-[12.5px] mb-3">{r.tagline}</div>
                <div className="mt-auto inline-flex items-center gap-2 text-[#7fa5e6] text-[12.5px]" style={{ fontFamily: "Sora, sans-serif" }}>
                  Explore <ArrowRight size={13} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="p-8 md:p-12 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-6" style={{ background: "linear-gradient(135deg, rgba(31,74,155,0.22), rgba(212,46,41,0.10))", border: "1px solid rgba(59,116,209,0.32)" }}>
            <div>
              <h3 className="text-white text-2xl font-semibold mb-1" style={{ fontFamily: "Sora, sans-serif" }}>
                Let’s talk about your requirements
              </h3>
              <p className="text-[#a6b3c8]">Get a scoped proposal from our engineering team.</p>
            </div>
            <Link to="/contact" className="btn-primary shrink-0">
              Contact Us <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
